// Renders each avatar's actual buildFisherman() rig to a small cached PNG
// (a real render of the in-game model, not a separate art asset, so it can
// never drift out of sync with what you actually look like). Used by
// hud.ts's avatar pickers in both the solo overlay and the multiplayer lobby.

import * as THREE from 'three';
import { buildFisherman } from '@entities/player';
import { PROP_SCALE } from '@world/terrain';
import { AVATARS, type AvatarDef } from '@entities/avatars';

const SIZE = 96;

function disposeObject(obj: THREE.Object3D): void {
  obj.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
    if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
    else mat?.dispose();
  });
}

function renderPortrait(renderer: THREE.WebGLRenderer, avatar: AvatarDef): string {
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 10);
  const yaw = 0.6, pitch = 0.4, dist = 2.0;
  camera.position.set(Math.sin(yaw) * Math.cos(pitch) * dist, 0.42 + Math.sin(pitch) * dist, Math.cos(yaw) * Math.cos(pitch) * dist);
  camera.lookAt(0, 0.42, 0);

  scene.add(new THREE.AmbientLight(0xffffff, 0.9));
  const sun = new THREE.DirectionalLight(0xffffff, 0.8);
  sun.position.set(2, 3, 2);
  scene.add(sun);

  const { fisher } = buildFisherman(avatar.visual);
  // Undo the in-world PROP_SCALE: the portrait camera is framed on a
  // full-size rig, so a third-scale one would sit tiny in the thumbnail.
  fisher.scale.divideScalar(PROP_SCALE);
  scene.add(fisher);

  renderer.setClearColor(0x000000, 0);
  renderer.render(scene, camera);
  const url = renderer.domElement.toDataURL('image/png');

  disposeObject(fisher);
  return url;
}

let cache: Map<string, string> | null = null;

export function getAvatarPortraits(): Map<string, string> {
  if (cache) return cache;
  cache = new Map();
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, preserveDrawingBuffer: true });
  renderer.setSize(SIZE, SIZE);
  AVATARS.forEach((av) => cache!.set(av.id, renderPortrait(renderer, av)));
  renderer.dispose();
  return cache;
}
